import '../css/pages/DocumentCamera.css';
import React, { useEffect, useRef, useState, useCallback } from "react";
import toast from "react-hot-toast";

/**
 * DocumentCameraModal Component.
 * Live camera video stream modal for scanning documents (ID, checks, license) with device selector.
 *
 * @component
 * @param {Object} props - React component props.
 * @param {boolean} props.open - Whether camera modal overlay is active.
 * @param {string} [props.title="Scan Document"] - Modal header title.
 * @param {string} [props.label="document"] - Document type label displayed in overlay instructions.
 * @param {number} [props.aspectRatio=2.15] - Target framing overlay aspect ratio.
 * @param {Function} props.onClose - Callback function triggered on modal close.
 * @param {Function} props.onCapture - Async callback receiving captured JPEG File object (`onCapture(file)`).
 * @returns {JSX.Element|null} Document camera modal element or null if closed.
 */
export default function DocumentCameraModal({
  open,
  title = "Scan Document",
  label = "document",
  aspectRatio = 2.15,
  onClose,
  onCapture,
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState("");

  // ── Device selector state ────────────────────────────────────────────
  const [cameras, setCameras] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");

  // Enumerate cameras once when modal opens
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        // Ask permission first so labels are populated
        await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter((d) => d.kind === "videoinput");
        setCameras(videoDevices);
        // Pick first device (or keep current selection)
        setSelectedDeviceId((prev) =>
          prev && videoDevices.find((d) => d.deviceId === prev)
            ? prev
            : videoDevices[0]?.deviceId || ""
        );
      } catch {
        // Permissions denied — startStream will handle the error display
      }
    })();
  }, [open]);

  // ── Start stream whenever selected device changes ────────────────────
  const startStream = useCallback(async (deviceId) => {
    // Stop any existing stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (!deviceId) return;

    setReady(false);
    setError("");

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    const baseConstraints = {
      deviceId: { exact: deviceId },
      width: { ideal: 1920, min: 640 },
      height: { ideal: 1080, min: 480 },
    };

    const videoConstraints =
      isSafari || isIOS
        ? baseConstraints
        : { ...baseConstraints, focusMode: "continuous" };

    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: false,
        });
      } catch {
        // Fallback: just use the deviceId without extra constraints
        stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: deviceId } },
          audio: false,
        });
      }

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute("playsinline", "");
        videoRef.current.setAttribute("webkit-playsinline", "");
        await videoRef.current.play();
        setReady(true);
      }
    } catch (err) {
      setError("Camera is unavailable. Please use the gallery upload instead.");
      console.error("Document camera start failed:", err.name, err.message);
    }
  }, []);

  // Start stream when device selection changes (and modal is open)
  useEffect(() => {
    if (!open || !selectedDeviceId) return;
    startStream(selectedDeviceId);
  }, [open, selectedDeviceId, startStream]);

  // Stop stream on close
  useEffect(() => {
    if (!open) {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setReady(false);
      setError("");
    }
  }, [open]);

  const capture = async () => {
    const video = videoRef.current;
    if (!video || !ready || capturing) return;
    setCapturing(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((resolve, reject) =>
        canvas.toBlob(
          (result) =>
            result
              ? resolve(result)
              : reject(new Error("Camera capture failed")),
          "image/jpeg",
          0.94,
        ),
      );
      const file = new File(
        [blob],
        `${label.replace(/\s+/g, "-")}-${Date.now()}.jpg`,
        { type: "image/jpeg", lastModified: Date.now() },
      );
      await onCapture(file);
      onClose();
    } catch (err) {
      toast.error("Could not capture image. Try again or choose from gallery.");
      console.error("Document camera capture failed", err);
    } finally {
      setCapturing(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay document-camera-overlay">
      <div className="document-camera-modal">
        <div className="document-camera-header">
          <div>
            <h2>{title}</h2>
            <p>Align the {label} inside the frame and hold steady.</p>
          </div>
          <button
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            disabled={capturing}
          >
            Close
          </button>
        </div>

        {/* ── Camera device selector ── */}
        {cameras.length > 0 && (
          <div
            style={{
              padding: "8px 18px 4px",
              display: "flex",
              alignItems: "center",
              gap: 10,
              borderBottom: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", whiteSpace: "nowrap" }}>
              📷 Camera:
            </span>
            <select
              value={selectedDeviceId}
              onChange={(e) => setSelectedDeviceId(e.target.value)}
              disabled={capturing}
              style={{
                flex: 1,
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.18)",
                color: "white",
                borderRadius: 6,
                padding: "5px 10px",
                fontSize: 12.5,
                outline: "none",
                cursor: "pointer",
              }}
            >
              {cameras.map((cam) => (
                <option
                  key={cam.deviceId}
                  value={cam.deviceId}
                  style={{ background: "#1c2b38", color: "white" }}
                >
                  {cam.label || `Camera ${cam.deviceId.substring(0, 8)}…`}
                </option>
              ))}
            </select>
          </div>
        )}

        <div
          className="document-camera-stage"
          style={{ "--scan-aspect": aspectRatio }}
        >
          {error ? (
            <div className="document-camera-error">{error}</div>
          ) : (
            <>
              <video
                ref={videoRef}
                playsInline
                muted
                autoPlay
                className="document-camera-video"
                style={{ WebkitPlaysinline: true }}
              />
              <div className="document-guide">
                <span className="corner corner-tl" />
                <span className="corner corner-tr" />
                <span className="corner corner-bl" />
                <span className="corner corner-br" />
                <div className="document-guide-text">
                  Fit all four edges inside
                </div>
              </div>
              {!ready && (
                <div className="document-camera-loading">
                  <span className="spinner" /> Starting camera...
                </div>
              )}
            </>
          )}
        </div>

        <div className="document-camera-actions">
          <button
            className="btn btn-outline"
            onClick={onClose}
            disabled={capturing}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={capture}
            disabled={!ready || capturing || Boolean(error)}
          >
            {capturing ? (
              <span className="flex items-center gap-2">
                <span className="spinner" /> Capturing...
              </span>
            ) : (
              "Capture"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
